import * as _ from 'lodash';
import * as Types from './model/types';
import * as CGTypes from './types';
import * as OpTypes from './opTypes';
import * as Graph from './graph';
import {ResolverContext} from './resolverContext';
import {Engine} from './engine';
import {ForwardGraph, ForwardOp} from './engine/forwardGraph';
import {Client} from './client';

type ReturnTypeFn<InputType extends CGTypes.InputTypes> = (
  argTypes: InputType,
  inputs: CGTypes.OpInputNodes<CGTypes.InputTypes>
) => Types.Type;

type ResolverFn<InputType extends CGTypes.InputTypes> = (
  inputs: CGTypes.OpResolverInputTypes<InputType>,
  inputTypes: InputType,
  rawInputs: CGTypes.OpResolverInputTypes<InputType>,
  forwardGraph: ForwardGraph,
  forwardOp: ForwardOp,
  context: ResolverContext,
  engine: () => Engine
) => Promise<any> | any;

// type ResolveTagFn<InputType extends CGTypes.InputTypes> = (
//   inputs: CGTypes.OpResolverInputTypes<InputType>
// ) => any;

type ResolveOutputTypeFn<InputType extends CGTypes.InputTypes> = (
  inputTypes: InputType,
  node: Types.OutputNode,
  executableNode: Types.OutputNode,
  client: Client
) => Promise<Types.Type>;

type MakeOpInputType<InputType extends CGTypes.InputTypes> = {
  [key in keyof InputType]: Types.Type;
};

interface OpFnOpts<InputType extends CGTypes.InputTypes> {
  name: string;
  argTypes: InputType;
  returnType: ReturnTypeFn<InputType>;
  resolver: ResolverFn<InputType>;
  renderInfo?: CGTypes.OpRenderInfo;
  resolveOutputType?: ResolveOutputTypeFn<InputType>;
  hidden?: boolean;
  description?: string;
  argDescriptions?: {
    [key: string]: string;
  };
  returnValueDescription?: string;
}

interface TagGetterOpFnOpts {
  name: string;
  tagName: string;
  tagType: Types.Type;
  hidden?: boolean;
  description?: string;
  argDescriptions?: {obj: string};
  returnValueDescription?: string;
}

export type MakeOpFn<InputType extends CGTypes.InputTypes> = (
  opts: OpFnOpts<InputType>
) => (
  inputs: CGTypes.OpInputNodes<MakeOpInputType<InputType>>
) => Types.OutputNode<Types.Type>;

export type MakeTaggingOpFn<InputType extends CGTypes.InputTypes> = (
  opts: OpFnOpts<InputType> & {
    // tagType: Types.Type;
    // resolveTag: ResolveTagFn<InputType>;
  }
) => (
  inputs: CGTypes.OpInputNodes<MakeOpInputType<InputType>>
) => Types.OutputNode<Types.Type>;

///// Basic op: nullable, taggable

const makeBasicOpReturnType =
  <InputType extends CGTypes.InputTypes>(returnType: ReturnTypeFn<InputType>) =>
  (inputNodes: CGTypes.OpInputNodes<CGTypes.InputTypes>) => {
    const arg0Name = Object.keys(inputNodes)[0];
    return OpTypes.nullableTaggable(inputNodes[arg0Name].type, t =>
      returnType(
        {
          // Note, specify first argument first to preserve ordering, since
          // we rely on object ordering sometimes.
          [arg0Name]: t,
          ..._.omit(
            _.mapValues(inputNodes, node => node.type),
            [arg0Name]
          ),
        } as any,
        inputNodes
      )
    );
  };

const makeBasicOpResolver =
  <InputType extends CGTypes.InputTypes>(resolver: ResolverFn<InputType>) =>
  (
    inputs: {[name: string]: any},
    forwardGraph: ForwardGraph,
    forwardOp: ForwardOp,
    context: ResolverContext,
    engine: () => Engine
  ) => {
    const arg0Name = Object.keys(inputs)[0];
    const inputTypes = _.mapValues(forwardOp.op.inputs, node => node.type);
    return OpTypes.nullableTaggableValAsync(inputs[arg0Name], v =>
      resolver(
        {
          [arg0Name]: v,
          ..._.mapValues(
            _.omit(inputs, [arg0Name]),
            OpTypes.getValueFromTaggedValue
          ),
        } as any,
        {
          [arg0Name]: OpTypes.nullableTaggableStrip(inputTypes[arg0Name]),
          ..._.omit(inputTypes, [arg0Name]),
        } as any,
        inputs as any,
        forwardGraph,
        forwardOp,
        context,
        engine
      )
    );
  };

const makeBasicOpResolveOutputType =
  <InputType extends CGTypes.InputTypes>(
    refine: ResolveOutputTypeFn<InputType>
  ) =>
  async (
    node: Types.OutputNode,
    executableNode: Types.OutputNode,
    client: Client
  ) => {
    const inputNodes = node.fromOp.inputs;
    const arg0Name = Object.keys(inputNodes)[0];
    return {
      ...node,
      type: await OpTypes.nullableTaggableAsync(inputNodes[arg0Name].type, v =>
        refine(
          {
            // Note, specify first argument first to preserve ordering, since
            // we rely on object ordering sometimes.
            [arg0Name]: v,
            ..._.omit(
              _.mapValues(inputNodes, n => n.type),
              [arg0Name]
            ),
          } as any,
          node,
          executableNode,
          client
        )
      ),
    };
  };

///// Standard op: nullable, taggable, mappable

const makeStandardOpReturnType =
  <InputType extends CGTypes.InputTypes>(returnType: ReturnTypeFn<InputType>) =>
  (inputNodes: CGTypes.OpInputNodes<CGTypes.InputTypes>) => {
    const arg0Name = Object.keys(inputNodes)[0];
    return OpTypes.mappableNullableTaggable(inputNodes[arg0Name].type, t =>
      returnType(
        {
          // Note, specify first argument first to preserve ordering, since
          // we rely on object ordering sometimes.
          [arg0Name]: t,
          ..._.omit(
            _.mapValues(inputNodes, node => node.type),
            [arg0Name]
          ),
        } as any,
        inputNodes
      )
    );
  };

const makeStandardOpResolver =
  <InputType extends CGTypes.InputTypes>(resolver: ResolverFn<InputType>) =>
  (
    inputs: {[name: string]: any},
    forwardGraph: ForwardGraph,
    forwardOp: ForwardOp,
    context: ResolverContext,
    engine: () => Engine
  ) => {
    const inputKeys = Object.keys(inputs);
    const arg0Name = inputKeys[0];

    // Note we do it in this order, rather than using spread, because
    // we must preserve object key ordering!
    const newInputTypes: {[name: string]: Types.Type} = {
      [arg0Name]: OpTypes.mappableNullableTaggableStrip(
        forwardOp.op.inputs[arg0Name].type
      ),
    };
    for (const key of inputKeys.slice(1)) {
      newInputTypes[key] = forwardOp.op.inputs[key].type;
    }

    // We must preserve object key order. The first key will be replaced
    // inside the callback below.
    const newInputs: {[name: string]: any} = {[arg0Name]: null};
    for (const key of inputKeys.slice(1)) {
      newInputs[key] = OpTypes.getValueFromTaggedValue(inputs[key]);
    }

    return OpTypes.mappableNullableTaggableValAsync(inputs[arg0Name], v => {
      newInputs[arg0Name] = v;
      return resolver(
        newInputs as any,
        newInputTypes as any,
        inputs as any,
        forwardGraph,
        forwardOp,
        context,
        engine
      );
    });
  };

const makeStandardOpResolveOutputType =
  <InputType extends CGTypes.InputTypes>(
    refine: ResolveOutputTypeFn<InputType>
  ) =>
  async (
    node: Types.OutputNode,
    executableNode: Types.OutputNode,
    client: Client
  ) => {
    const inputNodes = node.fromOp.inputs;
    const arg0Name = Object.keys(inputNodes)[0];
    return {
      ...node,
      type: await OpTypes.mappableNullableTaggableAsync(
        inputNodes[arg0Name].type,
        v =>
          refine(
            {
              // Note, specify first argument first to preserve ordering, since
              // we rely on object ordering sometimes.
              [arg0Name]: v,
              ..._.omit(
                _.mapValues(inputNodes, n => n.type),
                [arg0Name]
              ),
            } as any,
            node,
            executableNode,
            client
          )
      ),
    };
  };

///// Configurable Standard Op

const makeConfigurableStandardOpReturnType =
  <InputType extends CGTypes.InputTypes>(
    returnType: ReturnTypeFn<InputType>,
    typeConfig?: OpTypes.TypeProcessingConfig
  ) =>
  (inputNodes: CGTypes.OpInputNodes<CGTypes.InputTypes>) => {
    const arg0Name = Object.keys(inputNodes)[0];
    return OpTypes.mntTypeApply(
      inputNodes[arg0Name].type,
      t =>
        returnType(
          {
            // Note, specify first argument first to preserve ordering, since
            // we rely on object ordering sometimes.
            [arg0Name]: t,
            ..._.omit(
              _.mapValues(inputNodes, node => node.type),
              [arg0Name]
            ),
          } as any,
          inputNodes
        ),
      typeConfig
    );
  };

const makeTagConsumingStandardOpReturnType = <
  InputType extends CGTypes.InputTypes
>(
  returnType: ReturnTypeFn<InputType>
) => makeConfigurableStandardOpReturnType(returnType, {tags: true});

const makeConfigurableStandardOpResolver =
  <InputType extends CGTypes.InputTypes>(
    resolver: ResolverFn<InputType>,
    typeConfig?: OpTypes.TypeProcessingConfig
  ) =>
  (
    inputs: {[name: string]: any},
    forwardGraph: ForwardGraph,
    forwardOp: ForwardOp,
    context: ResolverContext,
    engine: () => Engine
  ) => {
    const inputKeys = Object.keys(inputs);
    const arg0Name = inputKeys[0];

    // Note we do it in this order, rather than using spread, because
    // we must preserve object key ordering!
    const newInputTypes: {[name: string]: Types.Type} = {
      [arg0Name]: OpTypes.mntTypeStrip(
        forwardOp.op.inputs[arg0Name].type,
        typeConfig
      ),
    };
    for (const key of inputKeys.slice(1)) {
      newInputTypes[key] = forwardOp.op.inputs[key].type;
    }

    // We must preserve object key order. The first key will be replaced
    // inside the callback below.
    const newInputs: {[name: string]: any} = {[arg0Name]: null};
    for (const key of inputKeys.slice(1)) {
      newInputs[key] = OpTypes.getValueFromTaggedValue(inputs[key]);
    }

    return OpTypes.mntValueApplyAsync(
      inputs[arg0Name],
      v => {
        newInputs[arg0Name] = v;
        return resolver(
          newInputs as any,
          newInputTypes as any,
          inputs as any,
          forwardGraph,
          forwardOp,
          context,
          engine
        );
      },
      typeConfig
    );
  };

const makeTagConsumingStandardOpResolver = <
  InputType extends CGTypes.InputTypes
>(
  resolver: ResolverFn<InputType>
) => makeConfigurableStandardOpResolver(resolver, {tags: true});

const makeConfigurableStandardOpResolveOutputType =
  <InputType extends CGTypes.InputTypes>(
    refine: ResolveOutputTypeFn<InputType>,
    typeConfig?: OpTypes.TypeProcessingConfig
  ) =>
  async (
    node: Types.OutputNode,
    executableNode: Types.OutputNode,
    client: Client
  ) => {
    const inputNodes = node.fromOp.inputs;
    const arg0Name = Object.keys(inputNodes)[0];
    return {
      ...node,
      type: await OpTypes.mntTypeApplyAsync(
        inputNodes[arg0Name].type,
        v =>
          refine(
            {
              // Note, specify first argument first to preserve ordering, since
              // we rely on object ordering sometimes.
              [arg0Name]: v,
              ..._.omit(
                _.mapValues(inputNodes, n => n.type),
                [arg0Name]
              ),
            } as any,
            node,
            executableNode,
            client
          ),
        typeConfig
      ),
    };
  };

const makeTagConsumingStandardOpResolveOutputType = <
  InputType extends CGTypes.InputTypes
>(
  refine: ResolveOutputTypeFn<InputType>
) => makeConfigurableStandardOpResolveOutputType(refine, {tags: true});

export const makeBasicOp = <I extends CGTypes.InputTypes>({
  name,
  argTypes,
  returnType,
  resolver,
  resolveOutputType,
  hidden,
  renderInfo,
  description,
  argDescriptions,
  returnValueDescription,
}: Parameters<MakeOpFn<I>>[0]) => {
  const arg0Name = Object.keys(argTypes)[0];
  const fullArgTypes = {
    [arg0Name]: Types.maybe(argTypes[arg0Name]),
    ..._.omit(argTypes, [arg0Name]),
  };
  return Graph.makeOp({
    name,
    argTypes: fullArgTypes,
    returnType: makeBasicOpReturnType(returnType),
    resolver: makeBasicOpResolver(resolver),
    resolveOutputType:
      resolveOutputType != null
        ? makeBasicOpResolveOutputType(resolveOutputType)
        : undefined,
    hidden,
    renderInfo,
    kind: 'basic',
    description,
    argDescriptions,
    returnValueDescription,
  });
};

// A basic op: nullable, taggable, that accepts a list as its first
// argument and returns a single item in response. Handles removing
// tags from the list elements.
export const makeBasicDimDownOp = <I extends CGTypes.InputTypes>({
  name,
  argTypes,
  returnType,
  resolver,
  resolveOutputType,
  hidden,
  description,
  argDescriptions,
  returnValueDescription,
}: Parameters<MakeOpFn<I>>[0]) => {
  const arg0Name = Object.keys(argTypes)[0];
  const fullArgTypes = {
    [arg0Name]: Types.maybe(argTypes[arg0Name]),
    ..._.omit(argTypes, [arg0Name]),
  };
  return Graph.makeOp({
    name,
    argTypes: fullArgTypes,
    returnType: makeBasicOpReturnType(returnType),
    resolver: makeBasicOpResolver<I>(
      (
        inputs,
        inputTypes,
        rawInputs,
        forwardGraph,
        forwardOp,
        context,
        engine
      ) => {
        const argName0 = Object.keys(inputs)[0];
        // Remove tags from objects in list
        const untaggedInputs = {
          [argName0]: inputs[argName0].map((v: any) =>
            OpTypes.getValueFromTaggedValue(v)
          ),
          ..._.omit(inputs, [argName0]),
        };
        const untaggedTypes = {
          [argName0]: OpTypes.mappableStrip(
            OpTypes.taggableStrip(inputTypes[argName0])
          ),
          ..._.omit(inputTypes, [argName0]),
        };
        return resolver(
          untaggedInputs as any,
          untaggedTypes as any,
          rawInputs,
          forwardGraph,
          forwardOp,
          context,
          engine
        );
      }
    ),
    resolveOutputType:
      resolveOutputType != null
        ? makeBasicOpResolveOutputType(resolveOutputType)
        : undefined,
    hidden,
    kind: 'basic-dim-down',
    description,
    argDescriptions,
    returnValueDescription,
  });
};

export const makeEqualOp = ({
  name,
  argType,
  hidden,
}: {
  name: string;
  argType: Types.Type;
  hidden?: boolean;
}) => {
  return Graph.makeOp({
    name,
    renderInfo: {
      type: 'binary',
      repr: '=',
    },
    argTypes: {
      lhs: Types.nullableOneOrMany(argType),
      rhs: Types.maybe(argType),
    },
    description: 'Determines equality of two values.',
    argDescriptions: {
      lhs: 'The first value to compare.',
      rhs: 'The second value to compare.',
    },
    returnValueDescription: 'Whether the two values are equal.',
    returnType: inputTypes =>
      // Note we use mappableNullTaggable for type but not for resolver!
      // We definitely don't want to use it in resolver, since we're trying
      // to actually compare nulls.
      // But our >=, <= etc ops return mappableNullableTaggable, we need to return
      // the same thing here to be able to suggest =/!= as replacement ops for
      // >=.

      OpTypes.mappableNullableTaggable(inputTypes.lhs.type, t => 'boolean'),
    resolver: ({lhs, rhs}) =>
      OpTypes.mappableTaggableVal(
        lhs,
        v => v === OpTypes.getValueFromTaggedValue(rhs)
      ),
    hidden,
    kind: 'equal',
  });
};

export const makeNotEqualOp = ({
  name,
  argType,
  hidden,
}: {
  name: string;
  argType: Types.Type;
  hidden?: boolean;
}) => {
  return Graph.makeOp({
    name,
    renderInfo: {
      type: 'binary',
      repr: '!=',
    },
    argTypes: {
      lhs: Types.nullableOneOrMany(argType),
      rhs: Types.maybe(argType),
    },
    description: 'Determines inequality of two values.',
    argDescriptions: {
      lhs: 'The first value to compare.',
      rhs: 'The second value to compare.',
    },
    returnValueDescription: 'Whether the two values are not equal.',
    returnType: inputTypes =>
      // See note in makeEqualOp
      OpTypes.mappableNullableTaggable(inputTypes.lhs.type, t => 'boolean'),
    resolver: ({lhs, rhs}) =>
      OpTypes.mappableTaggableVal(
        lhs,
        v => v !== OpTypes.getValueFromTaggedValue(rhs)
      ),
    hidden,
    kind: 'not-equal',
  });
};

export const makeStandardOp = <I extends CGTypes.InputTypes>({
  name,
  argTypes,
  returnType,
  renderInfo,
  resolver,
  resolveOutputType,
  hidden,
  description,
  argDescriptions,
  returnValueDescription,
}: Parameters<MakeOpFn<I>>[0]) => {
  const arg0Name = Object.keys(argTypes)[0];
  const fullArgTypes = {
    [arg0Name]: Types.nullableOneOrMany(argTypes[arg0Name]),
    ..._.omit(argTypes, [arg0Name]),
  };
  return Graph.makeOp({
    name,
    argTypes: fullArgTypes,
    renderInfo,
    returnType: makeStandardOpReturnType(returnType),
    resolver: makeStandardOpResolver(resolver),
    resolveOutputType:
      resolveOutputType != null
        ? makeStandardOpResolveOutputType(resolveOutputType)
        : undefined,
    hidden,
    kind: 'standard',
    description,
    argDescriptions,
    returnValueDescription,
  });
};

// Warning -- only use if you are sure this is the correct thing to use.
export const makeConfigurableStandardOp = <I extends CGTypes.InputTypes>({
  name,
  argTypes,
  returnType,
  renderInfo,
  resolver,
  resolveOutputType,
  hidden,
  typeConfig,
  description,
  argDescriptions,
  returnValueDescription,
}: Parameters<MakeOpFn<I>>[0] & {
  typeConfig?: OpTypes.TypeProcessingConfig;
}) => {
  const arg0Name = Object.keys(argTypes)[0];
  const fullArgTypes = {
    [arg0Name]: Types.nullableOneOrMany(argTypes[arg0Name]),
    ..._.omit(argTypes, [arg0Name]),
  };
  return Graph.makeOp({
    name,
    argTypes: fullArgTypes,
    renderInfo,
    returnType: makeConfigurableStandardOpReturnType(returnType, typeConfig),
    resolver: makeConfigurableStandardOpResolver(resolver, typeConfig),
    resolveOutputType:
      resolveOutputType != null
        ? makeConfigurableStandardOpResolveOutputType(
            resolveOutputType,
            typeConfig
          )
        : undefined,
    hidden,
    kind: 'standard-tag-consuming',
    description,
    argDescriptions,
    returnValueDescription,
  });
};

export const makeTagConsumingStandardOp = <I extends CGTypes.InputTypes>({
  name,
  argTypes,
  returnType,
  renderInfo,
  resolver,
  resolveOutputType,
  hidden,
  description,
  argDescriptions,
  returnValueDescription,
}: Parameters<MakeOpFn<I>>[0]) => {
  const arg0Name = Object.keys(argTypes)[0];
  const fullArgTypes = {
    [arg0Name]: Types.nullableOneOrMany(argTypes[arg0Name]),
    ..._.omit(argTypes, [arg0Name]),
  };
  return Graph.makeOp({
    name,
    argTypes: fullArgTypes,
    renderInfo,
    returnType: makeTagConsumingStandardOpReturnType(returnType),
    resolver: makeTagConsumingStandardOpResolver(resolver),
    resolveOutputType:
      resolveOutputType != null
        ? makeTagConsumingStandardOpResolveOutputType(resolveOutputType)
        : undefined,
    hidden,
    kind: 'standard-tag-consuming',
    description,
    argDescriptions,
    returnValueDescription,
  });
};

export const makeBinaryStandardOp = (
  repr: string,
  opts: Omit<Parameters<typeof makeStandardOp>[0], 'renderInfo'>
) => {
  return makeStandardOp({
    ...opts,
    renderInfo: {type: 'binary', repr},
  });
};

// Tags the result with its own inputs!
export const makeTaggingStandardOp = <I extends CGTypes.InputTypes>({
  name,
  argTypes,
  returnType,
  resolver,
  resolveOutputType,
  renderInfo,
  hidden,
  description,
  argDescriptions,
  returnValueDescription,
}: Parameters<MakeTaggingOpFn<I>>[0]) => {
  const arg0Name = Object.keys(argTypes)[0];
  const fullArgTypes = {
    [arg0Name]: Types.nullableOneOrMany(argTypes[arg0Name]),
    ..._.omit(argTypes, [arg0Name]),
  };
  return Graph.makeOp({
    name,
    argTypes: fullArgTypes,
    returnType: makeStandardOpReturnType<I>((inputTypes, inputs) =>
      Types.taggedValue(
        Types.typedDict(inputTypes),
        returnType(inputTypes, inputs)
      )
    ),
    renderInfo,
    resolver: makeStandardOpResolver<I>(
      async (
        inputs,
        inputTypes,
        rawInputs,
        forwardGraph,
        forwardOp,
        context,
        engine
      ) => {
        const resultValue = resolver(
          inputs as any,
          inputTypes,
          rawInputs,
          forwardGraph,
          forwardOp,
          context,
          engine
        );
        // if (Types.isConcreteTaggedValue(resultValue)) {
        //   // TODO: Do we need this?
        //   // If the resolver adds a tag then we don't call
        //   // resolveTag. This allows the resolver to override with a tag
        //   // based on the resolved value.
        //   return resultValue;
        // }
        return Types.concreteTaggedValue(
          {_opName: name, ...inputs},
          await resultValue
        );
        // return Types.concreteTaggedValue(resolveTag(inputs), resultValue);
      }
    ),
    resolveOutputType:
      resolveOutputType != null
        ? makeStandardOpResolveOutputType<I>(
            async (inputTypes, node, executableNode, context) =>
              Types.taggedValue(
                Types.typedDict(inputTypes),
                await resolveOutputType(
                  inputTypes,
                  node,
                  executableNode,
                  context
                )
              )
          )
        : undefined,
    hidden,
    kind: 'standard-tag',
    description,
    argDescriptions,
    returnValueDescription,
  });
};

// Handles union cases, and retains a tag's own tags
export const findNamedTagInType = (
  type: Types.Type,
  tagName: string,
  tagType: Types.Type
): Types.Type => {
  if (Types.isTaggedValue(type)) {
    if (
      Types.isAssignableTo2(
        type.tag,
        Types.typedDict({[tagName]: Types.maybe(tagType)})
      )
    ) {
      if (Types.isTaggedValue(type.tag)) {
        return Types.taggedValue(
          type.tag.tag,
          Types.typedDictPropertyTypes(type.tag)[tagName]
        );
      }
      return Types.typedDictPropertyTypes(type.tag)[tagName];
    } else {
      return findNamedTagInType(type.tag, tagName, tagType);
    }
  } else if (Types.isTaggedValueUnion(type)) {
    return Types.union(
      type.members.map(m => findNamedTagInType(m, tagName, tagType))
    );
  }
  return 'none';
};

export const findNamedTagInVal = (val: any, tagName: string): any => {
  if (!Types.isConcreteTaggedValue(val)) {
    return null;
  }
  const tag = OpTypes.getNamedTagFromValue(val, tagName);
  if (tag != null) {
    if (Types.isConcreteTaggedValue(tag)) {
      return Types.concreteTaggedValue(tag._tag, (tag._value as any)[tagName]);
    }
    return tag?.[tagName] ?? null;
  }
  return null;
};

export const makeTagGetterOp = ({
  name,
  tagName,
  tagType,
  hidden,
  description,
  argDescriptions,
  returnValueDescription,
}: TagGetterOpFnOpts) => {
  return Graph.makeOp({
    name,
    argTypes: {
      obj: Types.nullableOneOrMany(
        Types.taggedValue(Types.typedDict({[tagName]: tagType}), 'any')
      ),
    },
    returnType: inputs => {
      return OpTypes.nullable(inputs.obj.type, objTypeNonNull => {
        let outerTag: Types.Type | null = null;
        let objType = objTypeNonNull;

        if (Types.isTaggedValueLike(objTypeNonNull)) {
          outerTag = Types.taggedValueTagType(objTypeNonNull);
          objType = Types.taggedValueValueType(objTypeNonNull);
        }

        if (Types.isListLike(objType)) {
          const itemResult = OpTypes.mappableNullable(objType, t => {
            const res = findNamedTagInType(t, tagName, tagType);
            if (res !== 'none' && outerTag != null) {
              return Types.taggedValue(outerTag, res);
            }
            return res;
          });
          const innerResult = (itemResult as Types.ListType).objectType;
          if (innerResult !== 'none') {
            return itemResult;
          }
        }

        const outerResult = findNamedTagInType(
          objTypeNonNull,
          tagName,
          tagType
        );
        if (outerResult !== 'none') {
          return outerResult;
        }

        return 'none';
      });
    },
    resolver: inputs => {
      // TODO: make this properly respect types. When we get a tag's value from
      // a concrete tagged value, we do not enforce that it matches the requested type.

      return OpTypes.nullableVal(inputs.obj, objNonNull => {
        let outerTag: any = null;
        let obj = objNonNull;

        if (Types.isConcreteTaggedValue(objNonNull)) {
          outerTag = objNonNull._tag;
          obj = objNonNull._value;
        }

        if (_.isArray(obj)) {
          const itemResult = OpTypes.mappableNullableVal(obj, v => {
            const res = findNamedTagInVal(v, tagName);
            if (res != null && outerTag != null) {
              return Types.concreteTaggedValue(outerTag, res);
            }
            return res;
          });
          const itemResultValid = itemResult.some((item: any) => item != null);
          if (itemResultValid) {
            return itemResult;
          }
        }

        const tag = findNamedTagInVal(objNonNull, tagName);
        if (tag != null) {
          return tag;
        }

        return null;
      });
    },
    hidden,
    kind: 'tag-getter',
    description:
      description ??
      `Returns the first ${tagName} tag of type ${tagType} found in the given object.`,
    argDescriptions: argDescriptions ?? {
      obj: 'The object to get the tag value from.',
    },
    returnValueDescription:
      returnValueDescription ??
      `The first ${tagName} tag of type ${tagType} found in the given object.`,
  });
};
